import {
  addDays,
  addMonths,
  addQuarters,
  addSeconds,
  addWeeks,
  addYears,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  fromUnixTime,
  isBefore,
  isValid,
  Month,
  parse,
  parseISO,
  startOfDay,
  subDays,
  subMonths,
  subQuarters,
  subSeconds,
  subWeeks,
  subYears,
} from 'date-fns';
import { format as formatTz, fromZonedTime, toZonedTime } from 'date-fns-tz';
import { enUS, fr } from 'date-fns/locale';
import {
  dateFormat,
  timePickerDateFormat,
  timePickerDateFullFormat,
} from '@lib/enums/dateTime';
// date-fns v4 does not expose `date-fns/locale/types` via its package exports
// map (only per-locale entry points are exported), so the LocaleWidth union is
// inlined here instead of imported from that internal path.
type LocaleWidth = 'narrow' | 'short' | 'abbreviated' | 'wide' | 'any';

const customFr = {
  ...fr,
  localize: {
    ...fr.localize,
    month: (n: Month, options: { width?: LocaleWidth } = {}) => {
      const shortMonths = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      if (options.width === 'abbreviated') {
        return shortMonths[n];
      }
      return fr.localize?.month(n, options); // fallback to default
    },
  },
};

export type DateArg = string | null | undefined | Date;

export const CET = 'Europe/Paris';

export function isTimestampExpired(timestamp: number | string): boolean {
  const now = new Date();
  const timestampDate = fromUnixTime(Number(timestamp));
  return isBefore(timestampDate, now);
}

export function getLastTimeFromArray(dates?: string[]) {
  if (!dates) {
    return null;
  }

  const validDates = dates
    .map((date) => parseISO(date))
    .filter((date) => isValid(date));
  if (validDates.length === 0) {
    return null;
  }

  return validDates.reduce((acc, currentDate) =>
    isBefore(acc, currentDate) ? currentDate : acc,
  );
}

export function toIsoStringWithMicroseconds(date: Date): string {
  if (!date) return date;
  // Convert date to CET before formatting
  const cetDate = toZonedTime(date, CET);
  let isoString = cetDate.toISOString();
  const parts = isoString.split('.');
  const milliseconds = parts[1].substring(0, 3);
  let secondPartOfMilliseconds = '000';
  if (milliseconds === '999') {
    secondPartOfMilliseconds = '999';
  }
  // Construct ISO string with CET time and artificial microseconds
  isoString = `${parts[0]}.${milliseconds}${secondPartOfMilliseconds}Z`;
  return isoString;
}

export function formatTimeToISODateString(timeString: string): string {
  if (!timeString) return timeString;
  const [hours = 0, minutes = 0, seconds = 0] = timeString
    .split(':')
    .map(Number);
  // Create a date with the current date but specified time
  const now = new Date();
  now.setHours(hours, minutes, seconds, 0);
  // Convert this time to CET before formatting
  const cetDate = fromZonedTime(now, CET); // Note: This converts the local time to equivalent UTC time for CET zone
  return toIsoStringWithMicroseconds(cetDate);
}

export function transformDate(
  date: Date,
  setEndDate = false,
  setStartDate = false,
): string {
  let newDate = date;
  if (setEndDate) newDate = endOfDay(date);
  if (setStartDate) newDate = startOfDay(date);
  const formatPattern = setEndDate
    ? "yyyy-MM-dd'T'HH:mm:ss.SSS'999'XXX"
    : "yyyy-MM-dd'T'HH:mm:ss.SSS'000'XXX";
  // Format date in CET
  return formatTz(newDate, formatPattern, { timeZone: CET });
}

export function revertTransformDate(value: string) {
  return toZonedTime(parseISO(value), CET);
}

// A bare `YYYY-MM-DD` string fed directly into a DatePicker `value` prop is
// parsed as LOCAL midnight by `revertTransformDate` → its CET wall-clock
// conversion drops a day for users east of CET. The fix is NOT to route
// through `transformDate`: `date-fns-tz`'s `format` only uses its `timeZone`
// option to compute the offset suffix (XXX) — the actual date/time digits
// are produced by plain `date-fns` `format`, which reads the Date via the
// CALLER'S OWN system-timezone getters. So `transformDate(fromZonedTime(...))`
// still leaks the caller's local timezone into the digits and only happens to
// land on the right calendar day for some timezones. Instead, keep the `ymd`
// digits completely untouched (there's no time-of-day to convert) and only
// use timezone math to look up CET's offset for that date (which correctly
// handles the CET/CEST DST boundary), producing a value that round-trips
// through `revertTransformDate` correctly regardless of the caller's own
// timezone.
export function dateOnlyToDatePickerValue(ymd?: string | null): string | null {
  if (!ymd) return null;
  const cetInstant = fromZonedTime(parseISO(ymd), CET);
  const offset = formatTz(cetInstant, 'XXX', { timeZone: CET });
  return `${ymd}T00:00:00.000000${offset}`;
}

export function getFormattedDate(
  value?: DateArg,
  customFormat?: string,
  locale?: string,
) {
  if (!value) return null;
  const date = typeof value === 'string' ? revertTransformDate(value) : value;
  return formatTz(date, customFormat || dateFormat, {
    timeZone: CET,
    // @ts-ignore
    locale: locale === 'en' ? enUS : customFr,
  });
}

export function subYearsFn(value: DateArg, amount: number) {
  if (!value) return undefined;
  const date = typeof value === 'string' ? revertTransformDate(value) : value;
  return endOfMonth(subYears(date, amount));
}

export function subQuartersFn(value: DateArg, amount: number) {
  if (!value) return undefined;
  const date = typeof value === 'string' ? revertTransformDate(value) : value;
  return endOfMonth(subQuarters(date, amount));
}

export function subMonthsFn(value: DateArg, amount: number) {
  if (!value) return undefined;
  const date = typeof value === 'string' ? revertTransformDate(value) : value;
  return endOfMonth(subMonths(date, amount));
}

export function subWeeksFn(value: DateArg, amount: number) {
  if (!value) return undefined;
  const date = typeof value === 'string' ? revertTransformDate(value) : value;
  return endOfWeek(subWeeks(date, amount), { weekStartsOn: 1 });
}

export function subDaysFn(value: DateArg, amount: number) {
  if (!value) return undefined;
  const date = typeof value === 'string' ? revertTransformDate(value) : value;
  return subDays(date, amount);
}

export function subSecondsFn(value: DateArg, amount: number) {
  if (!value) return undefined;
  const date = typeof value === 'string' ? revertTransformDate(value) : value;
  return subSeconds(date, amount);
}

export function addYearsFn(value: DateArg, amount: number) {
  if (!value) return undefined;
  const date = typeof value === 'string' ? revertTransformDate(value) : value;
  return endOfMonth(addYears(date, amount));
}

export function addQuartersFn(value: DateArg, amount: number) {
  if (!value) return undefined;
  const date = typeof value === 'string' ? revertTransformDate(value) : value;
  return endOfMonth(addQuarters(date, amount));
}

export function addMonthsFn(value: DateArg, amount: number) {
  if (!value) return undefined;
  const date = typeof value === 'string' ? revertTransformDate(value) : value;
  return endOfMonth(addMonths(date, amount));
}

export function addWeeksFn(value: DateArg, amount: number) {
  if (!value) return undefined;
  const date = typeof value === 'string' ? revertTransformDate(value) : value;
  return endOfWeek(addWeeks(date, amount), { weekStartsOn: 1 });
}

export function addDaysFn(value: DateArg, amount: number) {
  if (!value) return undefined;
  const date = typeof value === 'string' ? revertTransformDate(value) : value;
  return addDays(date, amount);
}

export function addSecondsFn(value: DateArg, amount: number) {
  if (!value) return undefined;
  const date = typeof value === 'string' ? revertTransformDate(value) : value;
  return addSeconds(date, amount);
}

export function startOfDayFn(value: DateArg) {
  if (!value) return undefined;
  const date = typeof value === 'string' ? revertTransformDate(value) : value;
  return startOfDay(date);
}

export function endOfDayFn(value: DateArg) {
  if (!value) return undefined;
  const date = typeof value === 'string' ? revertTransformDate(value) : value;
  return endOfDay(date);
}

/**
 * Converts a time string to HH:mm format
 * @param time
 * @returns A string in HH:mm format
 */
export function convertTimeToHHmm(time?: unknown): string | undefined {
  try {
    if (typeof time === 'string' && !!time) {
      const parsedTime = parse(time, timePickerDateFullFormat, new Date());
      return format(parsedTime, timePickerDateFormat);
    }
    return undefined;
  } catch (error) {
    return undefined;
  }
}

/**
 * Converts a decimal time value to `hh:mm` format.
 * @param decimalValue - The decimal time value to convert (e.g., 10.10).
 * @returns The formatted time string in `hh:mm` format.
 */
export function convertDecimalHoursToTime(decimalValue: number): string {
  const isNegative = decimalValue < 0;
  const absoluteValue = Math.abs(decimalValue);

  const hours = Math.floor(absoluteValue);
  const minutes = Math.round((absoluteValue - hours) * 60);

  const formattedHours = String(hours).padStart(2, '0');
  const formattedMinutes = String(minutes).padStart(2, '0');

  return isNegative
    ? `-${formattedHours}:${formattedMinutes}`
    : `${formattedHours}:${formattedMinutes}`;
}

/**
 * Converts a decimal time value in minutes to `mm:ss` format.
 * @param decimalValue - The decimal time value to convert (e.g., 10.10).
 * @returns The formatted time string in `mm:ss` format.
 */
export function convertDecimalMinutesToTime(decimalValue: number): string {
  const isNegative = decimalValue < 0;
  const absoluteValue = Math.abs(decimalValue);

  const minutes = Math.floor(absoluteValue);
  const seconds = Math.round((absoluteValue - minutes) * 60);

  const formattedMinutes = String(minutes).padStart(2, '0');
  const formattedSeconds = String(seconds).padStart(2, '0');

  return isNegative
    ? `-${formattedMinutes}:${formattedSeconds}`
    : `${formattedMinutes}:${formattedSeconds}`;
}

/**
 * Converts a decimal time value in seconds to `ss:milliseconds` format.
 * @param decimalValue - The decimal time value to convert (e.g., 10.10).
 * @returns The formatted time string in `ss:milliseconds` format.
 */
export function convertDecimalSecondsToTime(decimalValue: number): string {
  const isNegative = decimalValue < 0;
  const absoluteValue = Math.abs(decimalValue);

  const seconds = Math.floor(absoluteValue);
  const milliseconds = Math.round((absoluteValue - seconds) * 1000);

  const formattedSeconds = String(seconds).padStart(2, '0');
  const formattedMilliseconds = String(milliseconds).padStart(3, '0'); // Ensure 3 digits for milliseconds

  return isNegative
    ? `-${formattedSeconds}:${formattedMilliseconds}`
    : `${formattedSeconds}:${formattedMilliseconds}`;
}

/**
 * Converts a seconds value to a formatted time string.
 * @param seconds - The seconds time value to convert.
 * @returns The formatted time string in `±Xd Xh Xm` format.
 */
export function convertSecondsToTime(
  seconds: number | null | undefined,
): string | undefined {
  if (seconds === null || seconds === undefined) return undefined;

  const isNegative = seconds < 0;
  const absoluteSeconds = Math.abs(seconds);

  const totalMinutes = Math.floor(absoluteSeconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const days = Math.floor(hours / 24);
  // const remainingSeconds = absoluteSeconds % 60;

  const formattedDays = `${days}d`;
  const formattedHours = `${hours % 24}h`;
  const formattedMinutes = `${minutes}m`;
  // const formattedSeconds = `${remainingSeconds}s`;

  return `${isNegative ? '-' : ''}${formattedDays} ${formattedHours} ${formattedMinutes}`;
}
